import { FileService } from '@business/services';
import { R } from '@core/utils/r';
import React, { memo, useCallback, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Alert, Card, Col, Row } from 'reactstrap';

interface DropZoneFileLineItemProps {
  file: DropZoneUploadedFile;
  index: number;
  removeFile(index: number): void;
  hasError?: boolean;
}

export const DropZoneFileLineItem = memo((props: DropZoneFileLineItemProps) => {
  const { file, index, removeFile, hasError = false } = props;

  return (
    <div className="mt-3" key={index}>
      <Card className={`mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete ${hasError ? 'border-danger' : ''}`}>
        <div className="p-2">
          <Row className="align-items-center">
            <Col className="col-auto">
              {FileService.isImage(file.name) ? (
                <img data-dz-thumbnail="" height="80" className="avatar-sm rounded bg-light" alt={file.name} src={file.preview} />
              ) : (
                <i className={`${FileService.getIcon(file.name)} font-size-24 `}></i>
              )}
            </Col>
            <Col className="">
              <a className={`font-weight-bold ${hasError ? 'text-danger' : 'text-muted'}`}>{file.name}</a>
              <p className={`mb-0 ${hasError ? 'text-danger' : ''}`}>
                <strong>{file.formattedSize}</strong>
              </p>
              {hasError && file.errorMessage && (
                <p className="mb-0 text-danger font-size-12">
                  <i className="bx bx-error-circle mr-1"></i>
                  {file.errorMessage}
                </p>
              )}
              <a onClick={() => removeFile(index)} style={{ float: 'right' }}>
                <i className="bx bx-trash font-size-16 text-danger"></i>
              </a>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
});

export interface DropZoneUploadedFile extends File {
  preview: string;
  formattedSize: string;
  errorMessage?: string;
  hasError?: boolean;
}

interface DropZoneFileProps {
  files: DropZoneUploadedFile[];
  setUploadedFiles(files: DropZoneUploadedFile[]): void;
  removeFile(index: number): void;
  label?: string;
  accept?: string;
  multiple?: boolean;
  small?: boolean;
  maxFileSize?: number; // in bytes
  maxTotalSize?: number; // in bytes
  onValidationError?(message: string): void;
}

export const DropZoneFiles = memo((props: DropZoneFileProps) => {
  const {
    files,
    setUploadedFiles,
    removeFile,
    label = 'Drop files here',
    multiple = true,
    small = false,
    maxFileSize,
    maxTotalSize,
    onValidationError,
  } = props;

  const [validationError, setValidationError] = useState<string | null>(null);

  // Calculate current total size
  const currentTotalSize = files.reduce((sum, file) => sum + file.size, 0);

  // Validate files
  const validateFiles = useCallback(
    (newFiles: File[], existingFiles: DropZoneUploadedFile[]) => {
      const errors: string[] = [];
      const validatedFiles: DropZoneUploadedFile[] = [];

      // Calculate new total size
      const existingTotalSize = existingFiles.reduce((sum, file) => sum + file.size, 0);
      let newTotalSize = existingTotalSize;

      for (const file of newFiles) {
        let fileError: string | undefined;
        let hasError = false;

        // Check total size
        newTotalSize += file.size;

        // Check individual file size
        if (maxFileSize && file.size > maxFileSize) {
          fileError = `File exceeds ${FileService.getSize(maxFileSize)} limit`;
          hasError = true;
          errors.push(`"${file.name}" exceeds maximum file size (${FileService.getSize(file.size)} > ${FileService.getSize(maxFileSize)})`);
        } else if (maxTotalSize && newTotalSize > maxTotalSize) {
          if (!hasError) {
            fileError = `Adding this file exceeds total upload limit`;
            hasError = true;
          }
          errors.push(
            `Adding "${file.name}" exceeds total upload limit (${FileService.getSize(newTotalSize)} > ${FileService.getSize(maxTotalSize)})`,
          );
        }

        // Create validated file object
        const validatedFile = R.assign(file, {
          preview: URL.createObjectURL(file),
          formattedSize: FileService.getSize(file.size),
          errorMessage: fileError,
          hasError,
        }) as DropZoneUploadedFile;

        validatedFiles.push(validatedFile);
      }

      return {
        files: validatedFiles,
        errors,
        hasErrors: errors.length > 0,
      };
    },
    [maxFileSize, maxTotalSize],
  );

  const onAddFiles = useCallback(
    (newFiles: File[]) => {
      const validation = validateFiles(newFiles, files);

      if (validation.hasErrors) {
        const errorMessage = validation.errors.join('\n');
        setValidationError(errorMessage);
        onValidationError?.(errorMessage);
      } else {
        setValidationError(null);
      }

      // Add all files (even with errors) so user can see them and remove if needed
      setUploadedFiles([...files, ...validation.files]);
    },
    [files, setUploadedFiles, validateFiles, onValidationError],
  );

  const onRemoveFile = useCallback(
    (index: number) => {
      removeFile(index);
      // Clear validation error when removing files
      setValidationError(null);
    },
    [removeFile],
  );

  return (
    <React.Fragment>
      <Dropzone onDrop={onAddFiles} accept={props.accept} multiple={multiple}>
        {({ getRootProps, getInputProps }) => (
          <div className="dropzone mb-2" style={{ minHeight: small ? 150 : 230 }}>
            <div className="dz-message needsclick" style={{ padding: small ? 0 : 30 }} {...getRootProps()}>
              <input {...getInputProps()} />
              <div className="dz-message needsclick">
                <div className="mb-3">
                  <i className="display-4 text-muted bx bxs-cloud-upload" />
                </div>
                <h4>{label}</h4>
                {(maxFileSize || maxTotalSize) && (
                  <div className="mt-2 font-size-12 text-muted">
                    {maxFileSize && <div>Max file size: {FileService.getSize(maxFileSize)}</div>}
                    {maxTotalSize && (
                      <div>
                        Max total: {FileService.getSize(maxTotalSize)}
                        {files.length > 0 && ` (${FileService.getSize(currentTotalSize)} used)`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Dropzone>

      {validationError && (
        <Alert color="danger" className="mb-3">
          {/* <i className="bx bx-error-circle mr-2"></i> */}
          {validationError.split('\n').map((line, idx) => (
            <div key={idx}>
              {line} <br />
            </div>
          ))}
        </Alert>
      )}

      <div className="dropzone-previews" id="file-previews">
        {files.map((file, index) => (
          <DropZoneFileLineItem key={index} index={index} file={file} removeFile={onRemoveFile} hasError={file.hasError} />
        ))}
      </div>
    </React.Fragment>
  );
});
